import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  LogOut,
  Package,
  Image,
  MessageSquare,
  Users,
  Settings,
  Plus,
  Edit,
  Trash2,
  Upload,
  X,
  Check,
  Loader2,
  Star,
  Mail,
  ImageIcon,
  Save
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full image URL for display
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('poochcrew_admin', 'true');
        onLogin();
      }
    } catch (err) {
      setError('Invalid password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f5f0e6] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#1e3a5f]">Admin Panel</h1>
          <p className="font-body text-[#1e3a5f]/60 mt-2">Enter your password to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              data-testid="admin-password-input"
              className="h-12 rounded-xl border-[#1e3a5f]/20"
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full h-12 bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white rounded-full font-semibold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Image Upload Component
const ImageUploader = ({ label, currentUrl, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState(currentUrl || '');

  useEffect(() => {
    setUrl(currentUrl || '');
  }, [currentUrl]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData);
      const newUrl = response.data.url;
      setUrl(newUrl);
      onUpdate(newUrl);
    } catch (error) {
      console.error('Upload failed:', error);
    }
    setUploading(false);
  };

  const handleUrlChange = (newUrl) => {
    setUrl(newUrl);
    onUpdate(newUrl);
  };

  // Get display URL for preview
  const displayUrl = getImageUrl(url);

  return (
    <div className="border border-[#c68b4d]/20 rounded-2xl p-4 space-y-3">
      <Label className="font-heading font-semibold text-[#1e3a5f]">{label}</Label>
      
      <div className="flex gap-4">
        {displayUrl && (
          <img
            src={displayUrl}
            alt={label}
            className="w-28 h-28 object-cover rounded-xl border border-[#c68b4d]/20"
          />
        )}
        <div className="flex-1 space-y-2">
          <Input
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="Paste image URL or upload"
            className="rounded-xl text-sm"
          />
          <label className="cursor-pointer inline-block">
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload
              </span>
            </Button>
          </label>
        </div>
      </div>
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    product || {
      name: '',
      description: '',
      price: '',
      category: 'petwear',
      image_url: '',
      sizes: [],
      colors: [],
      in_stock: true,
      featured: false,
    }
  );
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sizesInput, setSizesInput] = useState(product?.sizes?.join(', ') || '');
  const [colorsInput, setColorsInput] = useState(product?.colors?.join(', ') || '');

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload);
      setFormData({ ...formData, image_url: response.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      sizes: sizesInput.split(',').map((s) => s.trim()).filter(Boolean),
      colors: colorsInput.split(',').map((c) => c.trim()).filter(Boolean),
    };

    try {
      if (product?.id) {
        await axios.put(`${API}/products/${product.id}`, submitData);
      } else {
        await axios.post(`${API}/products`, submitData);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Product Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Cozy Navy Sweater"
            required
            className="rounded-xl"
          />
        </div>
        <div className="space-y-2">
          <Label>Price ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="45.00"
            required
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe your product..."
          rows={3}
          className="rounded-xl"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="petwear">Petwear</SelectItem>
              <SelectItem value="peoplewear">People Wear</SelectItem>
              <SelectItem value="matching">Matching Sets</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Sizes (comma separated)</Label>
          <Input
            value={sizesInput}
            onChange={(e) => setSizesInput(e.target.value)}
            placeholder="XS, S, M, L, XL"
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Colors (comma separated)</Label>
        <Input
          value={colorsInput}
          onChange={(e) => setColorsInput(e.target.value)}
          placeholder="Navy, Cream, Tan"
          className="rounded-xl"
        />
      </div>

      <div className="space-y-2">
        <Label>Product Image</Label>
        <div className="flex gap-4 items-start">
          {formData.image_url && (
            <img
              src={formData.image_url}
              alt="Preview"
              className="w-24 h-24 object-cover rounded-xl"
            />
          )}
          <div className="flex-1">
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="Image URL or upload"
              className="rounded-xl mb-2"
            />
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Image
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.in_stock}
            onCheckedChange={(checked) => setFormData({ ...formData, in_stock: checked })}
          />
          <Label>In Stock</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.featured}
            onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
          />
          <Label>Featured</Label>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : product?.id ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};

// Testimonial Form Component
const TestimonialForm = ({ testimonial, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    testimonial || {
      author: '',
      pet_name: '',
      content: '',
      rating: 5,
      image_url: '',
    }
  );
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload);
      setFormData({ ...formData, image_url: response.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (testimonial?.id) {
        await axios.put(`${API}/testimonials/${testimonial.id}`, { ...formData, id: testimonial.id });
      } else {
        await axios.post(`${API}/testimonials`, formData);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Customer Name</Label>
          <Input
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            placeholder="e.g., Sarah M."
            required
            className="rounded-xl"
          />
        </div>
        <div className="space-y-2">
          <Label>Pet Name</Label>
          <Input
            value={formData.pet_name}
            onChange={(e) => setFormData({ ...formData, pet_name: e.target.value })}
            placeholder="e.g., Cooper"
            required
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Review</Label>
        <Textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="What did they say about PoochCrew?"
          rows={3}
          required
          className="rounded-xl"
        />
      </div>

      <div className="space-y-2">
        <Label>Rating</Label>
        <Select
          value={formData.rating.toString()}
          onValueChange={(value) => setFormData({ ...formData, rating: parseInt(value) })}
        >
          <SelectTrigger className="rounded-xl w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[5, 4, 3, 2, 1].map(num => (
              <SelectItem key={num} value={num.toString()}>{num} Stars</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Photo</Label>
        <div className="flex gap-4 items-start">
          {formData.image_url && (
            <img
              src={formData.image_url}
              alt="Preview"
              className="w-20 h-20 object-cover rounded-full"
            />
          )}
          <div className="flex-1">
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="Image URL or upload"
              className="rounded-xl mb-2"
            />
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : testimonial?.id ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};

// Main Admin Dashboard
export const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const isAdmin = localStorage.getItem('poochcrew_admin');
    if (isAdmin) {
      setIsLoggedIn(true);
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, testimonialsRes, contactsRes, subscribersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/newsletter/subscribers`),
        axios.get(`${API}/settings`),
      ]);
      setProducts(productsRes.data);
      setTestimonials(testimonialsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data.subscribers || []);
      setSiteSettings(settingsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('poochcrew_admin');
    setIsLoggedIn(false);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      fetchData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      fetchData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleUpdateSetting = (key, value) => {
    setSiteSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, { ...siteSettings, id: 'site_settings' });
      alert('Images saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save');
    }
    setSavingSettings(false);
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => { setIsLoggedIn(true); fetchData(); }} />;
  }

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      {/* Header */}
      <header className="bg-white border-b border-[#c68b4d]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-[#1e3a5f]">Admin Panel</h1>
              <p className="font-body text-xs text-[#1e3a5f]/60">PoochCrew Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
          </div>
        ) : (
          <Tabs defaultValue="images" className="space-y-6">
            <TabsList className="bg-white rounded-full p-1 shadow-sm flex-wrap">
              <TabsTrigger value="images" className="rounded-full data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                <ImageIcon className="w-4 h-4 mr-2" />
                All Images
              </TabsTrigger>
              <TabsTrigger value="products" className="rounded-full data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                <Package className="w-4 h-4 mr-2" />
                Products ({products.length})
              </TabsTrigger>
              <TabsTrigger value="testimonials" className="rounded-full data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                <MessageSquare className="w-4 h-4 mr-2" />
                Testimonials ({testimonials.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="rounded-full data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                <Mail className="w-4 h-4 mr-2" />
                Messages ({contacts.length})
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="rounded-full data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" />
                Subscribers ({subscribers.length})
              </TabsTrigger>
            </TabsList>

            {/* Site Images Tab */}
            <TabsContent value="images">
              <div className="space-y-6">
                {/* Logo */}
                <div className="bg-white rounded-3xl shadow-sm p-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                    Website Logo
                  </h2>
                  <ImageUploader
                    label="Logo Image"
                    currentUrl={siteSettings.logo_image}
                    onUpdate={(url) => handleUpdateSetting('logo_image', url)}
                  />
                </div>

                {/* Hero Section */}
                <div className="bg-white rounded-3xl shadow-sm p-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                    Homepage Hero
                  </h2>
                  <ImageUploader
                    label="Hero Image"
                    currentUrl={siteSettings.hero_image}
                    onUpdate={(url) => handleUpdateSetting('hero_image', url)}
                  />
                </div>

                {/* Collection Images */}
                <div className="bg-white rounded-3xl shadow-sm p-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                    Collection Images
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ImageUploader
                      label="Petwear"
                      currentUrl={siteSettings.collection_petwear_image}
                      onUpdate={(url) => handleUpdateSetting('collection_petwear_image', url)}
                    />
                    <ImageUploader
                      label="People Wear"
                      currentUrl={siteSettings.collection_peoplewear_image}
                      onUpdate={(url) => handleUpdateSetting('collection_peoplewear_image', url)}
                    />
                    <ImageUploader
                      label="Matching Sets"
                      currentUrl={siteSettings.collection_matching_image}
                      onUpdate={(url) => handleUpdateSetting('collection_matching_image', url)}
                    />
                  </div>
                </div>

                {/* About Page Images */}
                <div className="bg-white rounded-3xl shadow-sm p-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                    About Page Images
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ImageUploader
                      label="About Image 1"
                      currentUrl={siteSettings.about_image_1}
                      onUpdate={(url) => handleUpdateSetting('about_image_1', url)}
                    />
                    <ImageUploader
                      label="About Image 2"
                      currentUrl={siteSettings.about_image_2}
                      onUpdate={(url) => handleUpdateSetting('about_image_2', url)}
                    />
                    <ImageUploader
                      label="About Image 3"
                      currentUrl={siteSettings.about_image_3}
                      onUpdate={(url) => handleUpdateSetting('about_image_3', url)}
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full px-8"
                    data-testid="save-settings-btn"
                  >
                    {savingSettings ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Save className="w-5 h-5 mr-2" />
                    )}
                    Save All Images
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products">
              <div className="bg-white rounded-3xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f]">
                    Manage Products
                  </h2>
                  <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setEditingProduct(null)}
                        className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
                        data-testid="add-product-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Product
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading">
                          {editingProduct ? 'Edit Product' : 'Add New Product'}
                        </DialogTitle>
                      </DialogHeader>
                      <ProductForm
                        product={editingProduct}
                        onSave={fetchData}
                        onClose={() => setProductDialogOpen(false)}
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="border border-[#c68b4d]/20 rounded-2xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`admin-product-${product.id}`}
                    >
                      <div className="flex gap-4">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-20 h-20 object-cover rounded-xl"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-[#1e3a5f] truncate">
                            {product.name}
                          </h3>
                          <p className="font-body text-sm text-[#1e3a5f]/60">
                            ${product.price?.toFixed(2)}
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {product.category}
                            </Badge>
                            {product.featured && (
                              <Badge className="bg-[#e76f51] text-xs">Featured</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 rounded-full"
                          onClick={() => {
                            setEditingProduct(product);
                            setProductDialogOpen(true);
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:bg-red-50 rounded-full"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {products.length === 0 && (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-[#c68b4d]/40 mx-auto mb-4" />
                    <p className="font-body text-[#1e3a5f]/60">No products yet. Add your first product!</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Testimonials Tab */}
            <TabsContent value="testimonials">
              <div className="bg-white rounded-3xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f]">
                    Customer Reviews
                  </h2>
                  <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setEditingTestimonial(null)}
                        className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
                        data-testid="add-testimonial-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Testimonial
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading">
                          {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                        </DialogTitle>
                      </DialogHeader>
                      <TestimonialForm
                        testimonial={editingTestimonial}
                        onSave={fetchData}
                        onClose={() => setTestimonialDialogOpen(false)}
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <div
                      key={testimonial.id}
                      className="border border-[#c68b4d]/20 rounded-2xl p-4 flex gap-4"
                    >
                      {testimonial.image_url && (
                        <img
                          src={testimonial.image_url}
                          alt={testimonial.pet_name}
                          className="w-16 h-16 object-cover rounded-full"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-heading font-semibold text-[#1e3a5f]">
                            {testimonial.author}
                          </span>
                          <span className="text-[#1e3a5f]/60">& {testimonial.pet_name}</span>
                          <div className="flex gap-0.5 ml-auto">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-[#c68b4d] fill-current" />
                            ))}
                          </div>
                        </div>
                        <p className="font-body text-sm text-[#1e3a5f]/70">"{testimonial.content}"</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => {
                            setEditingTestimonial(testimonial);
                            setTestimonialDialogOpen(true);
                          }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => handleDeleteTestimonial(testimonial.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {testimonials.length === 0 && (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-[#c68b4d]/40 mx-auto mb-4" />
                      <p className="font-body text-[#1e3a5f]/60">No testimonials yet. Add your first!</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <div className="bg-white rounded-3xl shadow-sm p-6">
                <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                  Contact Messages
                </h2>
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="border border-[#c68b4d]/20 rounded-2xl p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-heading font-semibold text-[#1e3a5f]">
                            {contact.name}
                          </span>
                          <span className="text-[#1e3a5f]/60 text-sm ml-2">{contact.email}</span>
                        </div>
                        <span className="text-xs text-[#1e3a5f]/40">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="font-body font-medium text-[#1e3a5f] text-sm mb-1">
                        {contact.subject}
                      </p>
                      <p className="font-body text-sm text-[#1e3a5f]/70">{contact.message}</p>
                    </div>
                  ))}
                  {contacts.length === 0 && (
                    <div className="text-center py-12">
                      <Mail className="w-12 h-12 text-[#c68b4d]/40 mx-auto mb-4" />
                      <p className="font-body text-[#1e3a5f]/60">No messages yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Subscribers Tab */}
            <TabsContent value="subscribers">
              <div className="bg-white rounded-3xl shadow-sm p-6">
                <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
                  Newsletter Subscribers
                </h2>
                <div className="space-y-2">
                  {subscribers.map((subscriber, index) => (
                    <div
                      key={subscriber.id || index}
                      className="flex items-center justify-between py-3 border-b border-[#c68b4d]/10 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#c68b4d]/10 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-[#c68b4d]" />
                        </div>
                        <span className="font-body text-[#1e3a5f]">{subscriber.email}</span>
                      </div>
                      <span className="text-xs text-[#1e3a5f]/40">
                        {subscriber.subscribed_at && new Date(subscriber.subscribed_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                  {subscribers.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-[#c68b4d]/40 mx-auto mb-4" />
                      <p className="font-body text-[#1e3a5f]/60">No subscribers yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
